import type { AvailableLocale } from '@/i18n/types'
import type { RankingParams } from '@/types'

export const LOCALE_COOKIE = 'selected_locale'
// used to be used for storing clid + windget_id, hence the cookie name "wid"
export const LANDING_CLID_COOKIE = 'landing_wid'
export const RANKING_CLUSTER_ID_COOKIE = 'rcid'
export const RANKING_AUDIENCE_COOKIE = 'audnc3' // number means we did reset audience for all users
const SESSION_COOKIE = 'pgsession'
export const PLATFORM_COOKIE = 'pg_plt'
export const AUID_COOKIE = 'auid'
export const UAC_COOKIE = 'pg_uac'
export const YAHOO_ID_SIGNAL_COOKIE = 'yahooId'
const SDK_UAC_COOKIE = 'pg_sdk_uac'
export const HASHED_EMAIL_COOKIE = 'pg_hue'
export const EXPERIMENTS_COOKIE = 'pg_exp'
export const AUTH_COOKIE = 'sb:refresh_token'

const hour = 1000 * 60 * 60
const day = 24 * hour
const UAC_PARAM_KEYS = ['source', 'medium', 'campaign', 'campaign_id', 'term', 'content'] as const

export function parseCookie(cookie: string) {
    return cookie.split('; ').reduce<Record<string, string>>((acc, el) => {
        const [key = '', value = ''] = el.split('=')
        acc[key] = decodeURIComponent(value)
        return acc
    }, {})
}

export function serializeCookies(cookies: Record<string, string | undefined>): string {
    return Object.entries(cookies)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
        .join('; ')
}

export function updateCookie(key: string, value: string, ttl: number) {
    const expires = new Date()
    expires.setTime(expires.getTime() + ttl)

    document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; Secure; SameSite=none`
}

export function deleteCookie(key: string) {
    updateCookie(key, '', -1)
}

export function encodeUACCookieValue(value: object): string {
    const bytes = new TextEncoder().encode(JSON.stringify(value))
    let binaryValue = ''
    for (const byte of bytes) {
        binaryValue += String.fromCharCode(byte)
    }
    return btoa(binaryValue)
}

export function decodeUACCookieValue(rawValue: string | undefined): Record<string, string> | null {
    if (!rawValue) {
        return null
    }

    try {
        const binaryValue = atob(rawValue)
        const bytes = Uint8Array.from(binaryValue, (character) => character.charCodeAt(0))
        let json: string
        try {
            json = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
        } catch {
            // pg_sdk_uac used browser-native Latin-1 encoding before the shared UTF-8 codec.
            json = binaryValue
        }
        const parsedValue: unknown = JSON.parse(json)
        if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
            return null
        }

        const params: Record<string, string> = {}
        for (const key of UAC_PARAM_KEYS) {
            const value = (parsedValue as Record<string, unknown>)[key]
            if (typeof value === 'string') {
                params[key] = value
            }
        }
        return params
    } catch {
        return null
    }
}

export function getUACFromCookies(cookie: string, cookieName: string) {
    return decodeUACCookieValue(parseCookie(cookie)[cookieName]) || {}
}

export function getUACFromCookies30d(cookie: string) {
    return getUACFromCookies(cookie, UAC_COOKIE)
}

export function getCurrentLocaleFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[LOCALE_COOKIE]
}

export function getLandingClidFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[LANDING_CLID_COOKIE]
}

export function updateLocaleCookie(value: AvailableLocale) {
    // Set expiration date (1 year from now)
    updateCookie(LOCALE_COOKIE, value, 365 * day)
}

export function updateLandingClidCookie(value: string) {
    // 30 days
    updateCookie(LANDING_CLID_COOKIE, value, 30 * day)
}

export function getAUID(cookie: string) {
    return parseCookie(cookie).auid
}

export function getRankingParamsFromCookies(cookie: string): RankingParams {
    const parsedCookie = parseCookie(cookie)
    return {
        clusterId: parsedCookie[RANKING_CLUSTER_ID_COOKIE],
        audience: parsedCookie[RANKING_AUDIENCE_COOKIE],
    }
}

export function updateRankingCookies({ clusterId, audience }: RankingParams) {
    if (clusterId) {
        updateCookie(RANKING_CLUSTER_ID_COOKIE, clusterId, 1 * hour)
    } else {
        deleteCookie(RANKING_CLUSTER_ID_COOKIE)
    }

    if (audience) {
        updateCookie(RANKING_AUDIENCE_COOKIE, audience, 30 * day)
    } else {
        deleteCookie(RANKING_AUDIENCE_COOKIE)
    }
}

export function getSessionCookie(): string | undefined {
    return parseCookie(document.cookie)[SESSION_COOKIE]
}

export function getPlatformFromCookies(cookie: string): string | undefined {
    const cookies = parseCookie(cookie)
    return cookies[PLATFORM_COOKIE]
}

export function setSessionCookie(sessionId: string) {
    const SESSION_INACTIVITY_TTL = hour / 2
    updateCookie(SESSION_COOKIE, sessionId, SESSION_INACTIVITY_TTL)
}

export function getSDKUACFromCookies(cookie: string): Record<string, string> | null {
    return decodeUACCookieValue(parseCookie(cookie)[SDK_UAC_COOKIE])
}

export function updateSDKUACCookie(value: Record<string, string>) {
    updateCookie(SDK_UAC_COOKIE, encodeUACCookieValue(value), 30 * day)
}

export function getHashedEmailFromCookie(): string | undefined {
    return parseCookie(document.cookie)[HASHED_EMAIL_COOKIE]
}

export function updateHashedEmailCookie(hashedEmail: string) {
    updateCookie(HASHED_EMAIL_COOKIE, hashedEmail, 30 * day)
}
